import { useDeleteDocumentPackageSummaryMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { DocumentPackageSummary } from '@consigli/types';
import { Trash2Icon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { ConfirmDialog, errorToast, successToast } from '@/components';

interface DeleteSummaryButtonProps {
  projectId: number;
  packageId: string;
  summary: DocumentPackageSummary | null;
}

export const DeleteSummaryButton = ({
  projectId,
  packageId,
  summary,
}: DeleteSummaryButtonProps) => {
  const [open, setOpen] = useState(false);
  const [deleteSummary, { isLoading }] = useDeleteDocumentPackageSummaryMutation();

  const handleDelete = useCallback(async () => {
    if (!summary) return;
    try {
      await deleteSummary({
        projectId,
        documentPackageId: packageId,
        summaryId: summary.id,
      }).unwrap();
      successToast({ message: 'Summary deleted successfully.' });
    } catch {
      errorToast({ message: 'Failed to delete summary. Please try again.' });
    } finally {
      setOpen(false);
    }
  }, [deleteSummary, projectId, packageId, summary]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button variant="destructive" size="sm" className="gap-2" disabled={!summary}>
          <Trash2Icon className="h-4 w-4" />
          Delete Summary
        </Button>
      }
      title="Delete summary?"
      description="Are you sure you want to delete this summary? This will also delete all sub-categories and finding references."
      confirmLabel="Delete"
      isLoading={isLoading}
      onConfirm={handleDelete}
    />
  );
};
